/* eslint-disable @typescript-eslint/no-explicit-any */
import { createTheme } from '@mui/material';
import { light, neutrals } from 'utils/styles/color';
import { palette as basePalette } from 'utils/styles/variables';
import { TenantConfig } from './types';

export const buildTenantTheme = (tenantConfig: TenantConfig) => {
  const {
    branding: { colors, typography },
  } = tenantConfig;

  const primaryFontFamily =
    typography?.primaryFontFamily || 'Poppins, sans-serif';
  const secondaryFontFamily =
    typography?.secondaryFontFamily || primaryFontFamily;
  const textFontFamily = typography?.textFontFamily || primaryFontFamily;
  const accentFontFamily = typography?.accentFontFamily || primaryFontFamily;
  const primaryFontWeight = typography?.primaryFontWeight || 700;
  const secondaryFontWeight = typography?.secondaryFontWeight || 400;
  const textFontWeight = typography?.textFontWeight || 500;
  const accentFontWeight = typography?.accentFontWeight || textFontWeight;
  const textColor = colors.text || '#2B2B2B';
  const borderAccentColor =
    colors.borderAccent || colors.accent || colors.primary;

  return createTheme({
    palette: {
      ...(basePalette as any),
      primary: {
        ...(basePalette.primary as any),
        main: colors.primary,
        secondary: colors.button,
      },
      secondary: {
        ...(basePalette.secondary as any),
        main: colors.secondary,
      },
      text: {
        primary: textColor,
        secondary: colors.secondary,
      },
      custom: {
        borderAccent: borderAccentColor,
      },
    } as any,
    typography: {
      fontFamily: textFontFamily,
      allVariants: {
        letterSpacing: 0,
      },
      h1: {
        fontFamily: primaryFontFamily,
        fontWeight: primaryFontWeight,
        color: '#7B828C',
        fontSize: '40px',
        lineHeight: '50px',
        marginBottom: '5px !important',
      },
      h2: {
        fontFamily: primaryFontFamily,
        fontWeight: primaryFontWeight,
        color: textColor,
        fontSize: '1.875rem',
        lineHeight: '48px',
      },
      h3: {
        fontFamily: primaryFontFamily,
        fontWeight: primaryFontWeight,
        color: textColor,
        fontSize: '1.125rem',
        lineHeight: '24px',
      },
      body1: {
        fontFamily: textFontFamily,
        fontWeight: textFontWeight,
        color: textColor,
        fontSize: '1rem',
        lineHeight: '16px',
      },
      body2: {
        fontFamily: secondaryFontFamily,
        fontWeight: secondaryFontWeight,
        color: '#666666',
        fontSize: '0.875rem',
        lineHeight: '24px',
      },
    },
    components: {
      MuiRadio: {
        defaultProps: {
          disableRipple: true,
        },
        styleOverrides: {
          root: {
            color: colors.primary,
            '&.Mui-checked': {
              color: colors.primary,
            },
          },
        },
      },
      MuiInputLabel: {
        styleOverrides: {
          root: {
            fontFamily: secondaryFontFamily,
            fontWeight: 400,
            color: '#999999',
          },
        },
      },
      MuiInputBase: {
        styleOverrides: {
          input: {
            fontWeight: 400,
          },
        },
      },
      MuiFormControlLabel: {
        styleOverrides: {
          label: {
            fontSize: '0.75rem',
            color: '#565148',
            fontWeight: 400,
          },
        },
      },
      MuiButtonBase: {
        defaultProps: {
          disableRipple: true,
        },
      },
      MuiButton: {
        defaultProps: {
          disableElevation: true,
        },
        styleOverrides: {
          root: {
            fontFamily: accentFontFamily,
            fontWeight: accentFontWeight,
            textTransform: 'none',
          },
          contained: {
            color: '#FFFFFF',
            backgroundColor: colors.button,
            '&:hover': {
              backgroundColor: colors.button,
            },
          },
        },
      },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            fontWeight: 400,
            fontSize: '0.875rem',
            '&:hover': {
              backgroundColor: light[100],
            },
            '&:focus': {
              backgroundColor: 'transparent',
            },
          },
        },
      },
      MuiListItemText: {
        styleOverrides: {
          root: {
            fontWeight: 400,
            fontSize: '0.875rem',
          },
          primary: {
            fontWeight: 400,
            fontSize: '0.875rem',
          },
        },
      },
      MuiCardHeader: {
        styleOverrides: {
          avatar: {
            color: borderAccentColor,
          },
          title: {
            color: neutrals[900],
            fontFamily: primaryFontFamily,
            fontSize: '1.125rem',
            fontStyle: 'normal',
            fontWeight: textFontWeight,
            lineHeight: '22px',
          },
          subheader: {
            fontSize: '0.75rem',
            fontWeight: 'normal',
            color: '#B0B6B6',
            marginTop: 8,
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          head: {
            color: '#7B828C',
            fontSize: '0.75rem',
            fontWeight: 500,
            whiteSpace: 'pre',
          },
          body: {
            color: '#565148',
            fontSize: '0.875rem',
            whiteSpace: 'pre',
          },
        },
      },
      MuiSwitch: {
        styleOverrides: {
          switchBase: {
            color: '#ccc',
          },
          colorPrimary: {
            '&.Mui-checked': {
              color: colors.primary,
            },
          },
          track: {
            opacity: 0.2,
            backgroundColor: 'grey',
            '.Mui-checked.Mui-checked + &': {
              opacity: 0.7,
              backgroundColor: colors.primary,
            },
          },
        },
      },
    },
  });
};
